/**
 * Model-related utility functions
 * Shared utilities for model management across components
 */

import type { ModelInfo } from '@gofranz/gpt-muse-common';
import { ModelCapability, ReasoningSupport } from '@gofranz/llm-core-common';

export interface BadgeData {
  key: string;
  color: string;
  label: string;
  icon?: string;
}

export interface IconData {
  key: string;
  type: 'text' | 'icon';
  label: string;
  color: string;
  text?: string;
  iconName?: string;
}

/**
 * Format pricing information for a model
 * @param model - The model to format pricing for
 * @param showFreeLabel - Whether to show "Free" for zero-cost models (default: false)
 * @returns Formatted pricing string
 */
export const formatPricing = (model: ModelInfo, showFreeLabel = false): string => {
  const { pricing } = model;
  const inputCost = pricing.min_input_cost;
  const outputCost = pricing.min_output_cost;

  if (showFreeLabel && inputCost === 0 && outputCost === 0) {
    return 'Free';
  }

  return `€${inputCost.toFixed(4)}/€${outputCost.toFixed(4)} per 1M tokens`;
};

/**
 * Get capability badge data for a model
 * @param model - The model to get badge data for
 * @returns Array of badge data objects
 */
export const getModelBadgeData = (model: ModelInfo): BadgeData[] => {
  const badges: BadgeData[] = [];

  if (model.free_tier) {
    badges.push({
      key: 'free',
      color: 'green',
      label: 'Free',
    });
  }

  if (model.capabilities.includes(ModelCapability.Vision)) {
    badges.push({
      key: 'vision',
      color: 'blue',
      label: 'Vision',
      icon: 'eye',
    });
  }

  if (model.capabilities.includes(ModelCapability.Tools)) {
    badges.push({
      key: 'tools',
      color: 'purple',
      label: 'Tools',
    });
  }

  if (model.capabilities.includes(ModelCapability.WebSearch)) {
    badges.push({
      key: 'websearch',
      color: 'teal',
      label: 'Web',
      icon: 'world',
    });
  }

  if (model.capabilities.includes(ModelCapability.ImageGeneration)) {
    badges.push({
      key: 'imagegen',
      color: 'pink',
      label: 'Image',
      icon: 'palette',
    });
  }

  if (model.reasoning_support === ReasoningSupport.Mandatory) {
    badges.push({
      key: 'reasoning',
      color: 'yellow',
      label: 'Reasoning',
      icon: 'bulb',
    });
  } else if (model.reasoning_support === ReasoningSupport.Optional) {
    badges.push({
      key: 'reasoning',
      color: 'orange',
      label: 'Reasoning*',
      icon: 'bulb',
    });
  }

  return badges;
};

/**
 * Get capability icon data for compact display
 * @param model - The model to get icon data for
 * @returns Array of icon data objects
 */
export const getModelIconData = (model: ModelInfo): IconData[] => {
  const icons: IconData[] = [];

  if (model.free_tier) {
    icons.push({
      key: 'free',
      type: 'text',
      label: 'Free tier available',
      color: 'var(--mantine-color-green-6)',
      text: 'F',
    });
  }

  if (model.capabilities.includes(ModelCapability.Vision)) {
    icons.push({
      key: 'vision',
      type: 'icon',
      label: 'Vision capabilities',
      color: 'var(--mantine-color-blue-6)',
      iconName: 'eye',
    });
  }

  if (model.capabilities.includes(ModelCapability.Tools)) {
    icons.push({
      key: 'tools',
      type: 'text',
      label: 'Tool usage',
      color: 'var(--mantine-color-purple-6)',
      text: 'T',
    });
  }

  if (model.capabilities.includes(ModelCapability.WebSearch)) {
    icons.push({
      key: 'websearch',
      type: 'icon',
      label: 'Web search',
      color: 'var(--mantine-color-teal-6)',
      iconName: 'world',
    });
  }

  if (model.capabilities.includes(ModelCapability.ImageGeneration)) {
    icons.push({
      key: 'imagegen',
      type: 'icon',
      label: 'Image generation',
      color: 'var(--mantine-color-pink-6)',
      iconName: 'palette',
    });
  }

  if (model.reasoning_support === ReasoningSupport.Mandatory) {
    icons.push({
      key: 'reasoning',
      type: 'icon',
      label: 'Reasoning (mandatory)',
      color: 'var(--mantine-color-yellow-6)',
      iconName: 'bulb',
    });
  } else if (model.reasoning_support === ReasoningSupport.Optional) {
    icons.push({
      key: 'reasoning',
      type: 'icon',
      label: 'Reasoning (optional)',
      color: 'var(--mantine-color-orange-6)',
      iconName: 'bulb',
    });
  }

  return icons;
};
